import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bell, Dumbbell, MessageSquareText, Mail, ClipboardCheck, Repeat, PlusCircle,
  Sparkles, CheckCheck, Cake, CalendarClock, Trash2, CornerUpLeft, AlertTriangle, Activity,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { syncBadge } from '@/lib/appBadge';
import { stageWorkoutCommentReply } from '@/lib/replyContext';
import { toast } from 'sonner';

// Per-type icon + accent. Keeps one chronological feed easy to scan (coach request).
const TYPE_META = {
  complete_workout: { Icon: Dumbbell, cls: 'bg-emerald-50 text-emerald-600' },
  workout_note: { Icon: MessageSquareText, cls: 'bg-sky-50 text-sky-600' },
  exercise_note: { Icon: MessageSquareText, cls: 'bg-sky-50 text-sky-600' },
  client_message: { Icon: Mail, cls: 'bg-violet-50 text-violet-600' },
  questionnaire_submitted: { Icon: ClipboardCheck, cls: 'bg-teal-50 text-teal-600' },
  substitution_flag: { Icon: Repeat, cls: 'bg-amber-50 text-amber-600' },
  client_added_exercise: { Icon: PlusCircle, cls: 'bg-amber-50 text-amber-600' },
  activity_replacement_request: { Icon: Repeat, cls: 'bg-amber-50 text-amber-600' },
  client_activity: { Icon: Dumbbell, cls: 'bg-emerald-50 text-emerald-600' },
  programme_ending: { Icon: CalendarClock, cls: 'bg-orange-50 text-orange-600' },
  package_renewal: { Icon: CalendarClock, cls: 'bg-orange-50 text-orange-600' },
  birthday_sent: { Icon: Cake, cls: 'bg-pink-50 text-pink-600' },
  birthday_draft: { Icon: Cake, cls: 'bg-pink-50 text-pink-600' },
  client_inactive_amber: { Icon: AlertTriangle, cls: 'bg-amber-100 text-amber-600' },
  client_inactive_red: { Icon: AlertTriangle, cls: 'bg-red-100 text-red-600' },
};
// Traffic-light engagement alerts get a restrained status accent (left border + chip) so the coach can
// scan the list and instantly spot early (Amber) vs significant (Red) inactivity.
const INACTIVITY_ACCENT = {
  client_inactive_amber: { border: 'border-l-2 border-l-amber-400', chip: 'bg-amber-100 text-amber-700', label: 'Amber' },
  client_inactive_red: { border: 'border-l-2 border-l-red-500', chip: 'bg-red-100 text-red-700', label: 'Red' },
};
const AI_SEV = {
  high: 'bg-red-50 text-red-600', medium: 'bg-amber-50 text-amber-600', low: 'bg-zinc-100 text-zinc-500',
};

function metaFor(n) {
  if (n.source === 'ai') return { Icon: Sparkles, cls: AI_SEV[n.severity] || AI_SEV.low };
  return TYPE_META[n.type] || { Icon: Bell, cls: 'bg-zinc-100 text-zinc-500' };
}

function relTime(iso) {
  if (!iso) return 'Needs attention';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d === 1 ? '' : 's'} ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} week${w === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString('en-GB');
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null); // canonical inactivity notification opened for review

  const load = useCallback(async () => {
    try { const { data } = await api.get('/notifications/feed'); setItems(data.items || []); setUnread(data.unread || 0); }
    catch (e) { console.error('Notifications load failed', e); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { load(); }, [location.pathname, load]); // refresh on navigation
  // The bell owns the Coach OS PWA app-icon badge: it always mirrors the canonical Notification Centre
  // unread total (messages + check-ins + workouts + alerts), cleared only when the true count is 0.
  useEffect(() => { syncBadge(unread); }, [unread]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [load]);

  // Canonical mark-as-read (optimistic) — reuses the one feed read mechanism; no second read system.
  const markRead = useCallback((n) => {
    if (!n || n.read) return;
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    api.post('/notifications/feed/read', { id: n.id, source: n.source }).catch((e) => console.error('mark read failed', e));
  }, []);

  const isInactivityNotif = (n) => n && (n.type === 'client_inactive_amber' || n.type === 'client_inactive_red');

  const openNotif = async (n) => {
    // Inactivity alerts open a review popup (read the full warning, then decide) instead of navigating
    // away immediately. All other notification types keep their existing click/deep-link behaviour.
    if (isInactivityNotif(n)) {
      setOpen(false);
      setDetail(n);
      return;
    }
    setOpen(false);
    markRead(n);
    if (n.path) navigate(n.path);
  };

  // Popup actions. Opening the popup = review only (never marks read). All three actions mark the
  // notification read. "View Client Activity" opens the new Client Activity tab (the notification's
  // canonical destination); "Re-engage Client" continues into the re-engagement workflow (Messages,
  // where the AI draft is generated/reviewed). Ignore just closes. None of these touch
  // meaningful_last_active, roster status, or the Inbox re-engagement intervention state.
  const ignoreDetail = () => { markRead(detail); setDetail(null); };
  const viewActivityDetail = () => {
    const cid = detail?.client_id; const p = detail?.path;
    markRead(detail); setDetail(null);
    navigate(cid ? `/coaching/${cid}?tab=activity` : (p || '/coaching'));
  };
  const reengageDetail = () => {
    const cid = detail?.client_id;
    markRead(detail); setDetail(null);
    if (cid) navigate(`/coaching/${cid}?tab=messages`);
  };

  const isCommentNotif = (n) => n && (n.type === 'workout_note' || n.type === 'exercise_note');

  // Build the quoted-reply context for a comment notification. Prefers the structured reply_context
  // (new notifications); falls back to deriving it from the title/preview/path so OLDER notifications
  // created before this feature still get a working "Reply in Messages" action.
  const commentReplyContext = (n) => {
    const rc = n.data && n.data.reply_context;
    if (rc && rc.client_id) return rc;
    if (!n.client_id || !isCommentNotif(n)) return null;
    const path = n.path || '';
    const logMatch = path.match(/[?&]log=([^&]+)/);
    const exMatch = path.match(/[?&]ex=([^&]+)/);
    const wkMatch = (n.title || '').match(/"([^"]+)"/);
    let exercise_name = exMatch ? decodeURIComponent(exMatch[1]) : null;
    if (!exercise_name && n.type === 'exercise_note') {
      const m = (n.title || '').match(/commented on (.+)$/);
      if (m) exercise_name = m[1];
    }
    return {
      kind: n.type === 'exercise_note' ? 'exercise_comment' : 'workout_comment',
      text: n.preview || '', client_id: n.client_id,
      workout_name: wkMatch ? wkMatch[1] : null,
      exercise_name,
      date: n.created_at ? String(n.created_at).slice(0, 10) : null,
      log_id: logMatch ? logMatch[1] : null,
    };
  };

  // Workflow 1: open the client's message thread with the comment pre-loaded as a quoted reply (never auto-sends).
  const replyInMessages = (n, e) => {
    e.stopPropagation();
    const rc = commentReplyContext(n);
    if (!rc || !rc.client_id) return;
    stageWorkoutCommentReply({
      client_id: rc.client_id, kind: rc.kind, text: rc.text,
      workout_name: rc.workout_name, exercise_name: rc.exercise_name, date: rc.date, log_id: rc.log_id,
    });
    setOpen(false);
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      api.post('/notifications/feed/read', { id: n.id, source: n.source }).catch(() => {});
    }
    navigate(`/coaching/${rc.client_id}?tab=messages`);
  };

  // Optimistic: reflect the change in the UI instantly, then persist in the background and reconcile
  // only on failure. The buttons vanish immediately (no unread / no read items), so there is no spinner
  // or wait for such a small set. AI keys currently in view are sent so the backend needn't recompute them.
  const markAll = async () => {
    const prevItems = items, prevUnread = unread;
    const aiKeys = items.filter((n) => n.source === 'ai' && !n.read).map((n) => n.id);
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try {
      await api.post('/notifications/feed/read', { all: true, ai_keys: aiKeys });
    } catch (e) {
      console.error('mark all read failed', e);
      setItems(prevItems); setUnread(prevUnread);
      toast.error("Couldn't mark all as read — please try again.");
    }
  };

  // Clear removes ONLY notifications already marked read (any type). Unread items stay. No confirmation.
  const clearRead = async () => {
    const prevItems = items;
    const aiKeys = items.filter((n) => n.source === 'ai' && n.read).map((n) => n.id);
    setItems((prev) => prev.filter((x) => !x.read));
    try {
      await api.post('/notifications/feed/clear-read', { ai_keys: aiKeys });
    } catch (e) {
      console.error('clear read failed', e);
      setItems(prevItems);
      toast.error("Couldn't clear notifications — please try again.");
    }
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-sm hover:bg-zinc-100 text-muted-foreground hover:text-foreground transition-colors">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center ring-2 ring-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} collisionPadding={20} className="w-[min(28rem,calc(100vw-2.5rem-env(safe-area-inset-left)-env(safe-area-inset-right)))] p-0 overflow-hidden rounded-lg shadow-xl" data-testid="notification-panel">
        <div className="px-4 py-3.5 border-b border-zinc-100 flex items-center justify-between gap-2">
          <h4 className="display text-sm">Notifications {unread > 0 && <span className="text-muted-foreground font-normal">· {unread} new</span>}</h4>
          <div className="flex items-center gap-1.5">
            {items.some((n) => !n.read) && (
              <button data-testid="notification-mark-all" onClick={markAll}
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground border border-zinc-200 hover:border-zinc-300 rounded-sm px-2.5 py-1 transition-colors">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
            {items.some((n) => n.read) && (
              <button data-testid="notification-clear-read" onClick={clearRead} title="Remove notifications you've already read"
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground border border-zinc-200 hover:border-zinc-300 rounded-sm px-2.5 py-1 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
        </div>
        <div className="max-h-[min(70vh,32rem)] overflow-auto overscroll-contain">
          {items.length === 0 ? (
            <p className="text-xs text-muted-foreground p-10 text-center" data-testid="notification-empty">You're all caught up.</p>
          ) : items.map((n) => {
            const { Icon, cls } = metaFor(n);
            const accent = INACTIVITY_ACCENT[n.type];
            return (
              <button key={`${n.source}-${n.id}`} onClick={() => openNotif(n)} data-testid={`notif-${n.type}`}
                className={`w-full text-left flex items-start gap-3 px-4 py-3.5 border-b border-zinc-50 last:border-0 hover:bg-zinc-50 transition-colors ${accent ? accent.border : ''} ${n.read ? '' : 'bg-sky-50/40'}`}>
                <span className={`mt-0.5 w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cls}`}><Icon className="w-4 h-4" /></span>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm leading-snug flex items-center gap-2 ${n.read ? 'text-foreground' : 'font-medium'}`}>
                    <span className="truncate">{n.title}</span>
                    {accent && <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${accent.chip}`}>{accent.label}</span>}
                  </div>
                  {n.preview && <div className="text-[12.5px] leading-relaxed text-muted-foreground italic truncate mt-0.5">“{n.preview}”</div>}
                  <div className="text-[11px] text-muted-foreground mt-1">{relTime(n.created_at)}</div>
                  {isCommentNotif(n) && n.client_id && (
                    <span role="button" tabIndex={0} data-testid="notif-reply-in-messages"
                      onClick={(e) => replyInMessages(n, e)}
                      onKeyDown={(e) => { if (e.key === 'Enter') replyInMessages(n, e); }}
                      className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-semibold text-sky-600 hover:text-sky-700 border border-sky-200 hover:border-sky-300 rounded-sm px-2 py-1 transition-colors cursor-pointer">
                      <CornerUpLeft className="w-3 h-3" /> Reply in Messages
                    </span>
                  )}
                </div>
                {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-red-500 shrink-0" data-testid={`notif-unread-${n.id}`} />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>

    {/* Inactivity alert review popup — full canonical text + Ignore / Re-engage. Uses the notification
        payload already in state (no extra fetch). Amber/Red severity preserved. */}
    <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
      <DialogContent className="w-[min(26rem,calc(100vw-2rem))] p-0 overflow-hidden gap-0" data-testid="inactivity-detail-dialog">
        {detail && (() => {
          const accent = INACTIVITY_ACCENT[detail.type] || INACTIVITY_ACCENT.client_inactive_amber;
          const isRed = detail.type === 'client_inactive_red';
          return (
            <>
              <DialogHeader className={`px-5 pt-5 pb-4 border-b border-zinc-100 ${accent.border}`}>
                <div className="flex items-center gap-2.5">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isRed ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                    <AlertTriangle className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0 text-left">
                    <DialogTitle className="text-base leading-tight flex items-center gap-2" data-testid="inactivity-detail-title">
                      <span className="truncate">{detail.title}</span>
                      <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${accent.chip}`}>{accent.label}</span>
                    </DialogTitle>
                    {detail.client_name && <p className="text-xs text-muted-foreground mt-0.5" data-testid="inactivity-detail-client">{detail.client_name}</p>}
                  </div>
                </div>
              </DialogHeader>
              <div className="px-5 py-4">
                <p className="text-sm leading-relaxed text-foreground" data-testid="inactivity-detail-body">{detail.preview}</p>
              </div>
              <DialogFooter className="px-5 pb-5 pt-1 flex-row flex-wrap justify-end gap-2 sm:gap-2">
                <Button variant="outline" size="sm" onClick={ignoreDetail} data-testid="inactivity-detail-ignore">Ignore</Button>
                <Button variant="outline" size="sm" onClick={viewActivityDetail} data-testid="inactivity-detail-activity"
                  className="border-zinc-300">
                  <Activity className="w-3.5 h-3.5 mr-1.5" /> View Client Activity
                </Button>
                <Button size="sm" onClick={reengageDetail} data-testid="inactivity-detail-reengage"
                  className={isRed ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}>
                  Re-engage Client
                </Button>
              </DialogFooter>
            </>
          );
        })()}
      </DialogContent>
    </Dialog>
    </>
  );
}
